/**
 * Canonical region metadata for residency classification.
 *
 * Every region code that appears in catalogue rows (AWS, GCP, Azure and the
 * geography codes some direct APIs publish) maps to the jurisdiction whose law
 * governs the data centre, its country and a broad region group.
 *
 * `jurisdiction` is deliberately legal rather than geographic: EU member states
 * share 'EU'; the United Kingdom ('UK'), Switzerland ('Switzerland') and Norway
 * ('Norway') are in Europe but not in the EU and are modelled separately.
 */
const EU = 'EU';
const AWS_REGIONS = [
    ['us-east-1', 'US East (N. Virginia)', 'US', 'US', 'Americas'],
    ['us-east-2', 'US East (Ohio)', 'US', 'US', 'Americas'],
    ['us-west-1', 'US West (N. California)', 'US', 'US', 'Americas'],
    ['us-west-2', 'US West (Oregon)', 'US', 'US', 'Americas'],
    ['us-gov-east-1', 'AWS GovCloud (US-East)', 'US', 'US', 'Americas'],
    ['us-gov-west-1', 'AWS GovCloud (US-West)', 'US', 'US', 'Americas'],
    ['ca-central-1', 'Canada (Central)', 'CA', 'Canada', 'Americas'],
    ['ca-west-1', 'Canada West (Calgary)', 'CA', 'Canada', 'Americas'],
    ['mx-central-1', 'Mexico (Central)', 'MX', 'Mexico', 'Americas'],
    ['sa-east-1', 'South America (São Paulo)', 'BR', 'Brazil', 'Americas'],
    ['eu-west-1', 'Europe (Ireland)', 'IE', EU, 'Europe'],
    ['eu-west-2', 'Europe (London)', 'GB', 'UK', 'Europe'],
    ['eu-west-3', 'Europe (Paris)', 'FR', EU, 'Europe'],
    ['eu-central-1', 'Europe (Frankfurt)', 'DE', EU, 'Europe'],
    ['eu-central-2', 'Europe (Zurich)', 'CH', 'Switzerland', 'Europe'],
    ['eu-north-1', 'Europe (Stockholm)', 'SE', EU, 'Europe'],
    ['eu-south-1', 'Europe (Milan)', 'IT', EU, 'Europe'],
    ['eu-south-2', 'Europe (Spain)', 'ES', EU, 'Europe'],
    ['ap-northeast-1', 'Asia Pacific (Tokyo)', 'JP', 'Japan', 'APAC'],
    ['ap-northeast-2', 'Asia Pacific (Seoul)', 'KR', 'South Korea', 'APAC'],
    ['ap-northeast-3', 'Asia Pacific (Osaka)', 'JP', 'Japan', 'APAC'],
    ['ap-east-1', 'Asia Pacific (Hong Kong)', 'HK', 'Hong Kong', 'APAC'],
    ['ap-east-2', 'Asia Pacific (Taipei)', 'TW', 'Taiwan', 'APAC'],
    ['ap-south-1', 'Asia Pacific (Mumbai)', 'IN', 'India', 'APAC'],
    ['ap-south-2', 'Asia Pacific (Hyderabad)', 'IN', 'India', 'APAC'],
    ['ap-southeast-1', 'Asia Pacific (Singapore)', 'SG', 'Singapore', 'APAC'],
    ['ap-southeast-2', 'Asia Pacific (Sydney)', 'AU', 'Australia', 'APAC'],
    ['ap-southeast-3', 'Asia Pacific (Jakarta)', 'ID', 'Indonesia', 'APAC'],
    ['ap-southeast-4', 'Asia Pacific (Melbourne)', 'AU', 'Australia', 'APAC'],
    ['ap-southeast-5', 'Asia Pacific (Malaysia)', 'MY', 'Malaysia', 'APAC'],
    ['ap-southeast-6', 'Asia Pacific (New Zealand)', 'NZ', 'New Zealand', 'APAC'],
    ['ap-southeast-7', 'Asia Pacific (Thailand)', 'TH', 'Thailand', 'APAC'],
    ['me-south-1', 'Middle East (Bahrain)', 'BH', 'Bahrain', 'Middle East'],
    ['me-central-1', 'Middle East (UAE)', 'AE', 'UAE', 'Middle East'],
    ['il-central-1', 'Israel (Tel Aviv)', 'IL', 'Israel', 'Middle East'],
    ['af-south-1', 'Africa (Cape Town)', 'ZA', 'South Africa', 'Africa'],
];
const GCP_REGIONS = [
    ['us-central1', 'Iowa', 'US', 'US', 'Americas'],
    ['us-east1', 'South Carolina', 'US', 'US', 'Americas'],
    ['us-east4', 'Northern Virginia', 'US', 'US', 'Americas'],
    ['us-east5', 'Columbus', 'US', 'US', 'Americas'],
    ['us-south1', 'Dallas', 'US', 'US', 'Americas'],
    ['us-west1', 'Oregon', 'US', 'US', 'Americas'],
    ['us-west2', 'Los Angeles', 'US', 'US', 'Americas'],
    ['us-west3', 'Salt Lake City', 'US', 'US', 'Americas'],
    ['us-west4', 'Las Vegas', 'US', 'US', 'Americas'],
    ['northamerica-northeast1', 'Montréal', 'CA', 'Canada', 'Americas'],
    ['northamerica-northeast2', 'Toronto', 'CA', 'Canada', 'Americas'],
    ['southamerica-east1', 'São Paulo', 'BR', 'Brazil', 'Americas'],
    ['europe-central2', 'Warsaw', 'PL', EU, 'Europe'],
    ['europe-north1', 'Hamina', 'FI', EU, 'Europe'],
    ['europe-southwest1', 'Madrid', 'ES', EU, 'Europe'],
    ['europe-west1', 'St. Ghislain', 'BE', EU, 'Europe'],
    ['europe-west2', 'London', 'GB', 'UK', 'Europe'],
    ['europe-west3', 'Frankfurt', 'DE', EU, 'Europe'],
    ['europe-west4', 'Eemshaven', 'NL', EU, 'Europe'],
    ['europe-west6', 'Zurich', 'CH', 'Switzerland', 'Europe'],
    ['europe-west8', 'Milan', 'IT', EU, 'Europe'],
    ['europe-west9', 'Paris', 'FR', EU, 'Europe'],
    ['europe-west12', 'Turin', 'IT', EU, 'Europe'],
    ['asia-east1', 'Taiwan', 'TW', 'Taiwan', 'APAC'],
    ['asia-east2', 'Hong Kong', 'HK', 'Hong Kong', 'APAC'],
    ['asia-northeast1', 'Tokyo', 'JP', 'Japan', 'APAC'],
    ['asia-northeast2', 'Osaka', 'JP', 'Japan', 'APAC'],
    ['asia-northeast3', 'Seoul', 'KR', 'South Korea', 'APAC'],
    ['asia-south1', 'Mumbai', 'IN', 'India', 'APAC'],
    ['asia-south2', 'Delhi', 'IN', 'India', 'APAC'],
    ['asia-southeast1', 'Singapore', 'SG', 'Singapore', 'APAC'],
    ['asia-southeast2', 'Jakarta', 'ID', 'Indonesia', 'APAC'],
    ['australia-southeast1', 'Sydney', 'AU', 'Australia', 'APAC'],
    ['australia-southeast2', 'Melbourne', 'AU', 'Australia', 'APAC'],
    ['me-central1', 'Doha', 'QA', 'Qatar', 'Middle East'],
    ['me-central2', 'Dammam', 'SA', 'Saudi Arabia', 'Middle East'],
    ['me-west1', 'Tel Aviv', 'IL', 'Israel', 'Middle East'],
    ['africa-south1', 'Johannesburg', 'ZA', 'South Africa', 'Africa'],
];
const AZURE_REGIONS = [
    ['eastus', 'East US', 'US', 'US', 'Americas'],
    ['eastus2', 'East US 2', 'US', 'US', 'Americas'],
    ['westus', 'West US', 'US', 'US', 'Americas'],
    ['westus2', 'West US 2', 'US', 'US', 'Americas'],
    ['westus3', 'West US 3', 'US', 'US', 'Americas'],
    ['centralus', 'Central US', 'US', 'US', 'Americas'],
    ['northcentralus', 'North Central US', 'US', 'US', 'Americas'],
    ['southcentralus', 'South Central US', 'US', 'US', 'Americas'],
    ['canadacentral', 'Canada Central', 'CA', 'Canada', 'Americas'],
    ['canadaeast', 'Canada East', 'CA', 'Canada', 'Americas'],
    ['brazilsouth', 'Brazil South', 'BR', 'Brazil', 'Americas'],
    ['westeurope', 'West Europe', 'NL', EU, 'Europe'],
    ['northeurope', 'North Europe', 'IE', EU, 'Europe'],
    ['swedencentral', 'Sweden Central', 'SE', EU, 'Europe'],
    ['francecentral', 'France Central', 'FR', EU, 'Europe'],
    ['germanywestcentral', 'Germany West Central', 'DE', EU, 'Europe'],
    ['italynorth', 'Italy North', 'IT', EU, 'Europe'],
    ['polandcentral', 'Poland Central', 'PL', EU, 'Europe'],
    ['spaincentral', 'Spain Central', 'ES', EU, 'Europe'],
    ['uksouth', 'UK South', 'GB', 'UK', 'Europe'],
    ['ukwest', 'UK West', 'GB', 'UK', 'Europe'],
    ['switzerlandnorth', 'Switzerland North', 'CH', 'Switzerland', 'Europe'],
    ['norwayeast', 'Norway East', 'NO', 'Norway', 'Europe'],
    ['japaneast', 'Japan East', 'JP', 'Japan', 'APAC'],
    ['japanwest', 'Japan West', 'JP', 'Japan', 'APAC'],
    ['koreacentral', 'Korea Central', 'KR', 'South Korea', 'APAC'],
    ['australiaeast', 'Australia East', 'AU', 'Australia', 'APAC'],
    ['australiasoutheast', 'Australia Southeast', 'AU', 'Australia', 'APAC'],
    ['southeastasia', 'Southeast Asia', 'SG', 'Singapore', 'APAC'],
    ['eastasia', 'East Asia', 'HK', 'Hong Kong', 'APAC'],
    ['centralindia', 'Central India', 'IN', 'India', 'APAC'],
    ['southindia', 'South India', 'IN', 'India', 'APAC'],
    ['uaenorth', 'UAE North', 'AE', 'UAE', 'Middle East'],
    ['qatarcentral', 'Qatar Central', 'QA', 'Qatar', 'Middle East'],
    ['southafricanorth', 'South Africa North', 'ZA', 'South Africa', 'Africa'],
];
/**
 * Multi-region geography codes used by direct APIs (OpenAI data residency
 * regions, BigQuery-style multi-regions, Bedrock geography labels).
 */
const GEOGRAPHY_CODES = [
    ['us', 'United States', 'US', 'US', 'Americas'],
    ['united-states', 'United States', 'US', 'US', 'Americas'],
    ['eu', 'European Union', undefined, EU, 'Europe'],
    ['europe', 'European Union', undefined, EU, 'Europe'],
    ['uk', 'United Kingdom', 'GB', 'UK', 'Europe'],
    ['jp', 'Japan', 'JP', 'Japan', 'APAC'],
    ['japan', 'Japan', 'JP', 'Japan', 'APAC'],
    ['au', 'Australia', 'AU', 'Australia', 'APAC'],
    ['australia', 'Australia', 'AU', 'Australia', 'APAC'],
    ['ca', 'Canada', 'CA', 'Canada', 'Americas'],
    ['canada', 'Canada', 'CA', 'Canada', 'Americas'],
    ['in', 'India', 'IN', 'India', 'APAC'],
    ['kr', 'South Korea', 'KR', 'South Korea', 'APAC'],
    ['sg', 'Singapore', 'SG', 'Singapore', 'APAC'],
];
function build(platform, rows) {
    return rows.map(([code, name, country, jurisdiction, regionGroup]) => [
        code,
        { code, platform, name, ...(country ? { country } : {}), jurisdiction, regionGroup },
    ]);
}
export const REGION_METADATA = Object.freeze(Object.fromEntries([
    ...build('geography', GEOGRAPHY_CODES),
    ...build('azure', AZURE_REGIONS),
    ...build('gcp', GCP_REGIONS),
    ...build('aws', AWS_REGIONS),
]));
/** Codes that mean "anywhere" rather than a place. */
export const GLOBAL_REGION_CODES = new Set(['global', 'any', 'worldwide']);
export function getRegionMetadata(regionCode) {
    if (!regionCode)
        return undefined;
    return REGION_METADATA[regionCode.trim().toLowerCase()];
}
/** Jurisdiction label for a region code, or 'Unknown' when it is not mapped. */
export function getRegionJurisdiction(regionCode) {
    if (regionCode && GLOBAL_REGION_CODES.has(regionCode.trim().toLowerCase()))
        return 'Global';
    return getRegionMetadata(regionCode)?.jurisdiction ?? 'Unknown';
}
//# sourceMappingURL=regionMetadata.js.map